import { Controller } from "@hotwired/stimulus";
import Rails from "@rails/ujs";

export default class extends Controller {
  static targets = [ "result", "ignore", "plot", "form", "x", "y", "xvalue", "yvalue", "xtext", "ytext", "xhint", "yhint", "dot", "error", "input", "requiredMarker" ]

  connect() {
    var _this = this,
        inprocess = false,
        savetimer;

    if (_this.hasResultTarget) {
      _this.savedResult = _this.resultTarget.value;
      _this.savingResult = null;
      _this.saveFailed = false;
      _this.setSaveState("saved");
      _this.flushSaveHandler = () => _this.flushSave();
      _this.element.addEventListener("reviews:flush", _this.flushSaveHandler);
    }

    document.addEventListener("MDCAutoInit:End", () => {
      _this.mainInit(_this);

      if (_this.hasInputTarget && _this.inputTarget.dataset.textinput) {
        _this.updateTextInputValidity(_this.inputTarget);
      }
    });

    if (_this.hasInputTarget ) { 

      if (_this.inputTarget.dataset.textinput) {
        _this.updateTextInputValidity(_this.inputTarget);

        _this.inputTarget.addEventListener("input", function (e) {
          _this.updateTextInputValidity(_this.inputTarget);
        });

        _this.inputTarget.addEventListener("blur", function (e) {
          setTimeout(() => _this.updateTextInputValidity(_this.inputTarget), 0);
        });

        _this.inputTarget.addEventListener("keyup", function (e) { 
          clearTimeout(savetimer);
          savetimer = setTimeout(() => {
            _this.submitResult(_this.inputTarget);
          }, 1000);
        });

      }
      _this.inputTarget.addEventListener("change", (event) => {
        clearTimeout(savetimer);
        _this.updateTextInputValidity(_this.inputTarget);
        _this.updateRequiredState(_this.inputTarget);
        _this.submitResult(_this.inputTarget);
      });

    }

    if (_this.hasFormTarget ) { 
      _this.formTarget.addEventListener("ajax:success", (event) => {
        const [data, status, xhr] = event.detail;
        _this.errorTarget.classList.add("hidden");

        _this.savedResult = _this.savingResult;
        _this.savingResult = null;
        _this.saveFailed = false;
        const continueSaving = _this.resultTarget.value != _this.savedResult;

        if (continueSaving) {
          _this.setSaveState("dirty");
        } else {
          _this.setSaveState("saved");
        }

        console.log("Review: success - ", status);

        if (continueSaving) {
          _this.startSave();
        }
        _this.finishSave(true);

      });

      _this.formTarget.addEventListener("ajax:error", (event) => {
        console.log("Review: ERROR - not saved");
        _this.errorTarget.classList.remove("hidden");
        _this.savingResult = null;
        _this.saveFailed = true;
        _this.setSaveState("failed");
        _this.finishSave(false);
      });

    }

  }

  disconnect() {
    if (this.flushSaveHandler != null) {
      this.element.removeEventListener("reviews:flush", this.flushSaveHandler);
    }
  }


  save(event) {
    var _this = this;
    var event = new Event('change');
    _this.inputTarget.dispatchEvent(event);
  }

  submit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (event.currentTarget.dataset.submitted == "true") {
      return;
    }

    const dialog = event.currentTarget.closest(".mdc-dialog");
    const validation = this.reviewValidation(dialog);

    if (!validation.valid) {
      this.showReviewValidation(dialog, validation);
      return;
    }

    this.hideReviewValidation(dialog);
    this.flushTextInputs(dialog);
    this.flushReviewSaves(dialog);

    event.currentTarget.dataset.submitted = "true";
    event.currentTarget.disabled = true;
    event.currentTarget.classList.add("clicked");
    event.currentTarget.querySelector(".mdc-button__label").innerHTML = "Saving";

    if (this.reviewSavesComplete(dialog)) {
      location.reload();
    }
  }

  reviewValidation(dialog) {
    const criteria = dialog == null ? [] : [...dialog.querySelectorAll(".criteria")];
    const requiredCriteria = criteria.filter((criterion) => {
      return criterion.querySelector('[data-reviews-target~="result"][data-review-required="true"]') != null;
    });
    const missingCriteria = requiredCriteria.filter((criterion) => !this.criteriaIsAnswered(criterion));

    if (missingCriteria.length > 0) {
      return {
        valid: false,
        missingCriteria: missingCriteria,
        message: "Answer all required questions before submitting."
      };
    }

    if (requiredCriteria.length == 0 && !criteria.some((criterion) => this.criteriaIsAnswered(criterion))) {
      return {
        valid: false,
        missingCriteria: criteria.slice(0, 1),
        message: "Answer at least one question before submitting."
      };
    }

    return {valid: true, missingCriteria: [], message: ""};
  }

  criteriaIsAnswered(criterion) {
    const input = criterion.querySelector('[data-reviews-target~="input"]');
    const result = criterion.querySelector('[data-reviews-target~="result"]');

    if (input?.type == "select-multiple") {
      return input.selectedOptions.length > 0;
    }

    if (input?.type == "checkbox") {
      return result != null && !this.unansweredResult(result.value);
    }

    if (input != null) {
      return !this.unansweredResult(input.value);
    }

    return result?.dataset.reviewAnswered == "true" && !this.unansweredResult(result.value);
  }

  unansweredResult(result) {
    const normalizedResult = (result ?? "").trim();
    return normalizedResult.length == 0 || normalizedResult == "[]";
  }

  showReviewValidation(dialog, validation) {
    const error = dialog?.querySelector("[data-review-submit-error]");
    if (error != null) {
      error.textContent = validation.message;
      error.classList.remove("hidden");
    }

    validation.missingCriteria.forEach((criterion) => {
      criterion.querySelectorAll('[data-reviews-target~="requiredMarker"]').forEach((marker) => {
        marker.classList.remove("invisible");
      });

      const textInput = criterion.querySelector('[data-textinput="true"]');
      if (textInput != null) {
        textInput.dispatchEvent(new Event("input"));
      }
    });

    const firstMissingCriterion = validation.missingCriteria[0];
    if (firstMissingCriterion == null) return;

    firstMissingCriterion.scrollIntoView({behavior: "smooth", block: "center"});
    const focusTarget = firstMissingCriterion.querySelector(
      'textarea, input:not([type="hidden"]), button, .mdc-slider, [tabindex]'
    );
    focusTarget?.focus();

    const textInput = firstMissingCriterion.querySelector('[data-textinput="true"]');
    textInput?.reportValidity();
  }

  hideReviewValidation(dialog) {
    const error = dialog?.querySelector("[data-review-submit-error]");
    error?.classList.add("hidden");
  }

  flushTextInputs(dialog) {
    if (dialog == null) return;

    dialog.querySelectorAll('[data-textinput="true"]').forEach((input) => {
      const result = input.closest(".criteria")?.querySelector('[data-reviews-target~="result"]');
      if (result != null && input.value != result.value) {
        input.dispatchEvent(new Event("change"));
      }
    });
  }

  flushReviewSaves(dialog) {
    dialog?.querySelectorAll(".criteria").forEach((criterion) => {
      criterion.dispatchEvent(new CustomEvent("reviews:flush"));
    });
  }

  flushSave() {
    if (!this.hasResultTarget || this.savingResult != null) return;

    if (!this.saveFailed && this.resultTarget.value == this.savedResult) {
      this.setSaveState("saved");
      return;
    }

    this.startSave();
  }

  startSave() {
    if (!this.hasFormTarget || !this.hasResultTarget || this.savingResult != null) return;

    if (!this.saveFailed && this.resultTarget.value == this.savedResult) {
      this.setSaveState("saved");
      return;
    }

    this.savingResult = this.resultTarget.value;
    this.saveFailed = false;
    this.setSaveState("saving");
    Rails.fire(this.formTarget, 'submit');
  }

  setSaveState(state) {
    this.element.dataset.reviewSaveState = state;
  }

  reviewSavesComplete(dialog) {
    if (dialog == null) return false;

    return [...dialog.querySelectorAll(".criteria")].every((criterion) => {
      return criterion.dataset.reviewSaveState == "saved";
    });
  }

  finishSave(success) {
    const submitButton = document.getElementById("submit-reviews-button");
    if (submitButton == null) return;

    if (!success) {
      this.resetSubmitButton(submitButton);
      return;
    }

    if (submitButton.dataset.submitted == "true") {
      const dialog = submitButton.closest(".mdc-dialog");
      this.flushTextInputs(dialog);
      this.flushReviewSaves(dialog);
      const validation = this.reviewValidation(dialog);

      if (validation.valid && this.reviewSavesComplete(dialog)) {
        location.reload();
      } else if (!validation.valid) {
        this.showReviewValidation(dialog, validation);
        this.resetSubmitButton(submitButton);
      }
    }
  }

  resetSubmitButton(submitButton) {
    submitButton.classList.remove("clicked");
    submitButton.dataset.submitted = "false";
    submitButton.disabled = false;
    submitButton.querySelector(".mdc-button__label").innerHTML = "Submit";
  }

  updateTextInputValidity(target) {
    if (!target.dataset.textinput || target.dataset.reviewRequired != "true") return;

    let field = target.closest(".mdc-text-field"),
        helper = target.closest(".flex1")?.querySelector(".mdc-text-field-helper-text--validation-msg"),
        empty = target.value.trim().length == 0;

    target.setCustomValidity(empty ? "Required - not answered" : "");

    if (field?.MDCTextField != null) {
      field.MDCTextField.valid = target.validity.valid;
    } else if (field != null) {
      field.classList.toggle("mdc-text-field--invalid", empty);
    }

    if (helper != null) {
      helper.classList.toggle("hidden", !empty);
    }
  }

  updateRequiredState(target) {
    if (target.dataset.reviewRequired != "true") return;

    let answered = false;

    if (target.type == "select-multiple") {
      answered = target.selectedOptions.length > 0;
    } else if (target.type == "checkbox") {
      answered = true;
    } else {
      answered = target.value.trim().length > 0;
    }

    if (this.hasRequiredMarkerTarget) {
      this.requiredMarkerTarget.classList.toggle("invisible", answered);
    }

  }

  ignore(event) {
    var _this = this;
    this.ignoreTarget.value = true;
    Rails.fire(this.formTarget, 'submit');
  }

  clicked(event) {
    var xpr = ((event.clientX - this.plotTarget.getBoundingClientRect().left) / this.plotTarget.getBoundingClientRect().width) * 100;
    var ypr = ((event.clientY - this.plotTarget.getBoundingClientRect().top) / this.plotTarget.getBoundingClientRect().height) * 100;

    this.submitXYResult(parseFloat(xpr.toFixed(1)), 100 - parseFloat(ypr.toFixed(1)))
    this.initSliders(this.resultTarget);
  }

  mainInit(_this) {

    if (_this.hasPlotTarget) {
      _this.initPlot(_this.plotTarget)
      _this.initSliders(_this.resultTarget);

      _this.plotTarget.addEventListener("click", function(event) {
        _this.clicked(event);
        _this.initPlot(_this.plotTarget)
      });



      let slider_x = _this.xTarget.MDCSlider;
      let slider_y = _this.yTarget.MDCSlider;

      _this.dotTarget.addEventListener("click", function(event) {
        event.stopPropagation();
        _this.submitXYResult(slider_x.value, slider_y.value);
        _this.initPlot(_this.plotTarget)
      });

      slider_x.listen('MDCSlider:change', () => {
        _this.submitXYResult(slider_x.value, slider_y.value);
        _this.initPlot(_this.plotTarget)
      });

      slider_y.listen('MDCSlider:change', () => {
        _this.submitXYResult(slider_x.value, slider_y.value);
        _this.initPlot(_this.plotTarget)
      });
      
    }

  }

  submitXYResult(x, y) {
    var res = {"x": x, "y": y};

    this.resultTarget.value = JSON.stringify(res);
    this.resultTarget.dataset.reviewAnswered = "true";
    this.updateRequiredState(this.resultTarget);

    this.setXYSliderValue(x,y);

    this.setSaveState("dirty");
    this.flushSave();

    document.getElementById("ignore_review").classList.remove("hidden");
  }

  submitResult(target) {
    var result = null;

    if (target.type == 'select-multiple')
      result = JSON.stringify([...target.selectedOptions].map(o => o.value));
    else if (target.type == 'checkbox')
      result = target.checked.toString();
    else
      result = target.value;

    this.resultTarget.value = result;
    this.resultTarget.dataset.reviewAnswered = (!this.unansweredResult(result)).toString();
    this.updateRequiredState(target);

    this.setSaveState("dirty");
    this.flushSave();

    if (document.getElementById("ignore_review") != null) {
      document.getElementById("ignore_review").classList.remove("hidden");
    }
  }

  initSliders(target) {
    var [x,y] = this.pointParser(target);
      
    
    this.xTarget.MDCSlider.value = Math.round(x);
    this.yTarget.MDCSlider.value = Math.round(y);

    this.setXYSliderValue(x,y);
  }

  setXYSliderValue(x,y) {

    if (x != undefined) {
      let text = "",
          hint = "";

      x = Math.round(x);

      if (x >= 90) {
        text = "Exceptional";
        hint = "The innovation could be easily adopted and adapted in contexts I'm familiar with to an excellent standard and sustained for the foreseeable future.";
      } else if (x >= 70) {
        text = "High";
        hint = "As per “Exceptional,” except only achieved for part(s) of the innovation.";
      } else if (x >= 50) {
        text = "Moderate";
        hint = "The innovation could be adopted and adapted in contexts I'm familiar with to a high standard and sustained for the foreseeable future.";
      } else if (x >= 30) {
        text = "Limited";
        hint = "As per “Insufficient,” however, the innovation has some aspects which are at the “Moderate” level.";
      } else {
        text = "Insufficient";
        hint = "The innovation could not be easily and sustainably adopted and adapted with similar quality standards in contexts I'm familiar with.";
      }

      this.xvalueTarget.innerHTML = x;
      this.xtextTarget.innerHTML = text;
      this.xhintTarget.innerHTML = hint;
    }

    if (y != undefined) {
      let text = "",
          hint = "";
      y = Math.round(y);

      if (y >= 90) {
        text = "Exceptional";
        hint = "The innovation could have a significant and meaningful impact in contexts I'm familiar with to an excellent standard above what commonly occurs.";
      } else if (y >= 70) {
        text = "High";
        hint = "As per “Exceptional,” except only achieved for part(s) of the innovation.";
      } else if (y >= 50) {
        text = "Moderate";
        hint = "The innovation could have a meaningful impact in contexts I'm familiar with to a standard above what commonly occurs.";
      } else if (y >= 30) {
        text = "Limited";
        hint = "As per “Insufficient,” however, the innovation has some aspects which are at the “Moderate” level.";
      } else {
        text = "Insufficient";
        hint = "The innovation would not have a meaningful impact in contexts I'm familiar with to a standard above what commonly occurs.";
      }

      this.yvalueTarget.innerHTML = y;
      this.ytextTarget.innerHTML = text;
      this.yhintTarget.innerHTML = hint;
    }

  }

  pointParser(target) {
    const point = target.value;
    if (point.length == 0) return [50, 50];

    const point_json = JSON.parse(point),
          x = point_json.x,
          y = point_json.y;

    return [x, y];
  }

  initPlot(target) {

    const [x, y] = this.pointParser(this.resultTarget),
          map = target.closest(".criteria"),
          dot_css = `left: ${x}%; top: ${100 - y}%;`;

    map.querySelector(".dot").style.cssText = dot_css;
    
    var clip_css = `width: ${x}%; height: ${y}%;`;
    map.querySelector(".clip").style.cssText += clip_css; 

    map.querySelectorAll(".label").forEach((el) => {
      el.classList.remove('font-600');
    });
    let label_x = 1;
    let label_y = 1;

    label_x = Math.trunc(Math.round(x)/20);
    label_y = Math.trunc(Math.round(y)/20);

    if (x >= 90) 
      label_x = 5
    else if (x >= 70)
      label_x = 4
    else if (x >= 50)
      label_x = 3
    else if (x >= 30)
      label_x = 2
    else 
      label_x = 1

    if (y >= 90) 
      label_y = 5
    else if (y >= 70)
      label_y = 4
    else if (y >= 50)
      label_y = 3
    else if (y >= 30)
      label_y = 2
    else 
      label_y = 1
    map.querySelector(`.label.x${label_x}`).classList.add('font-600');
    map.querySelector(`.label.y${label_y}`).classList.add('font-600');

  }

}
